import AsyncStorage from '@react-native-async-storage/async-storage';

// ==================== EXISTING SERVICE IMPORTS ====================
import databaseService from './DatabaseService';
import apiService from './ApiService';
import syncService from './SyncService';                       // ✅ Uses the new SyncService.js
import businessApiService from './BusinessApiService';
import businessProfileApiService from './BusinessProfileApiService';
import submissionService from './SubmissionService';
import arPortfolioService from './ARPortfolioService';
import assessmentResultsIntegrationService from './AssessmentResultsIntegrationService';
import businessIntegrationService from './BusinessIntegrationService';
import businessProfessionalIntegration from './BusinessProfessionalIntegration';
import careerCoachingService from './CareerCoachingService';
import comprehensiveIntegrationService from './ComprehensiveIntegrationService';
import dashboardIntegrationService from './DashboardIntegrationService';
import databaseIntegrationService from './DatabaseIntegrationService';
import educationManagementService from './EducationManagementService';
import experienceManagementService from './ExperienceManagementService';
import interviewIntegrationService from './InterviewIntegrationService';
import interviewWorkflowAutomation from './InterviewWorkflowAutomation';
import portfolioDatabase from './PortfolioDatabase';
import profileManagementService from './ProfileManagementService';
import psychometricTestService from './PsychometricTestService';
import skillManagementService from './SkillManagementService';
import paymentService from './PaymentService';
import learningApiService from './LearningApiService';
import erpApiService from './ERPApiService';

// Job search API services (exported as objects)
import * as jobSearchAPI from './jobSearchAPI';
import * as googleJobsAPI from './googleJobsAPI';
import * as fullTimeJobsAPI from './fullTimeJobsAPI';
import * as partTimeJobsAPI from './partTimeJobsAPI';
import * as entryLevelJobsAPI from './entryLevelJobsAPI';

// Utility services
import cacheManager from './cacheManager';
import imageUploadService from './ImageUploadService';

// ==================== PREVIOUSLY ADDED SERVICES ====================
import businessNavigationService from './BusinessNavigationService';
import * as adzunaAPI from './adzunaAPI';
import aiInterviewService from './AIInterviewService';
import * as aiService from './aiService';
import AIVideoInterviewServiceClass from './AIVideoInterviewService';
import * as allJobsSearchAPI from './allJobsSearchAPI';
import apiClient from './apiClient';
import { scaleUpAPI } from './api-scaleup';
import { DataManagementService } from './DataManagementService';
import learningApiClient from './learningApiClient';
import schedulingService from './SchedulingService';
import { SQLiteService } from './SQLiteService';

import activityTracker from './UnifiedActivityTracker';
import UnifiedIntegrationServiceClass from './UnifiedIntegrationService';
import UnifiedJobApplicationService from './UnifiedJobApplicationService';
import * as unifiedJobAPI from './unifiedJobAPI';

// ==================== NEW SERVICES (from recent files) ====================
import websiteAPI from './website/WebsiteAPI';
import analyticsService from './website/AnalyticsService';

// ==================== INSTANTIATE CLASS-BASED SERVICES ====================
const aIVideoInterviewService = new AIVideoInterviewServiceClass();
const dataManagementService = new DataManagementService();
const sqliteService = new SQLiteService();
const unifiedIntegrationService = new UnifiedIntegrationServiceClass();

// ==================== SERVICE MANAGER CLASS ====================
class ServiceManager {
  constructor() {
    this.services = new Map();
    this.initialized = false;
  }

  register(name, instance, dependencies = []) {
    if (this.services.has(name)) {
      console.warn(`Service "${name}" already registered. Overwriting.`);
    }
    this.services.set(name, {
      instance,
      dependencies,
      initialized: false,
    });
  }

  async initialize() {
    if (this.initialized) {
      console.log('ServiceManager already initialized.');
      return;
    }

    console.log('🚀 ServiceManager initializing...');

    const order = this._resolveDependencyOrder();
    for (const name of order) {
      const entry = this.services.get(name);
      if (!entry.initialized && entry.instance.initialize) {
        try {
          console.log(`Initializing ${name}...`);
          await entry.instance.initialize();
          entry.initialized = true;
          console.log(`✅ ${name} initialized.`);
        } catch (error) {
          console.error(`❌ Failed to initialize ${name}:`, error);
        }
      } else if (entry.instance.initialize) {
        console.log(`${name} already initialized.`);
      }
    }

    this.initialized = true;
    console.log('✅ ServiceManager initialized.');
  }

  get(name) {
    const entry = this.services.get(name);
    if (!entry) {
      throw new Error(`Service "${name}" not registered.`);
    }
    return entry.instance;
  }

  _resolveDependencyOrder() {
    const names = Array.from(this.services.keys());
    const visited = new Set();
    const order = [];

    const dfs = (name, stack) => {
      if (stack.includes(name)) {
        throw new Error(`Circular dependency detected: ${stack.join(' -> ')} -> ${name}`);
      }
      if (visited.has(name)) return;
      visited.add(name);
      const entry = this.services.get(name);
      for (const dep of entry.dependencies) {
        dfs(dep, [...stack, name]);
      }
      order.push(name);
    };

    for (const name of names) {
      dfs(name, []);
    }
    return order;
  }
}

const serviceManager = new ServiceManager();

// ==================== REGISTER ALL SERVICES ====================

// Core services
serviceManager.register('databaseService', databaseService, []);
serviceManager.register('apiService', apiService, []);
serviceManager.register('syncService', syncService, ['databaseService', 'apiService']);

// Business services
serviceManager.register('businessApiService', businessApiService, ['apiService']);
serviceManager.register('businessProfileApiService', businessProfileApiService, []);
serviceManager.register('submissionService', submissionService, []);
serviceManager.register('erpApiService', erpApiService, []);
serviceManager.register('paymentService', paymentService, []);

// Professional / candidate services
serviceManager.register('arPortfolioService', arPortfolioService, []);
serviceManager.register('assessmentResultsIntegrationService', assessmentResultsIntegrationService, []);
serviceManager.register('careerCoachingService', careerCoachingService, []);
serviceManager.register('educationManagementService', educationManagementService, []);
serviceManager.register('experienceManagementService', experienceManagementService, []);
serviceManager.register('interviewIntegrationService', interviewIntegrationService, []);
serviceManager.register('interviewWorkflowAutomation', interviewWorkflowAutomation, ['interviewIntegrationService']);
serviceManager.register('portfolioDatabase', portfolioDatabase, []);
serviceManager.register('profileManagementService', profileManagementService, []);
serviceManager.register('psychometricTestService', psychometricTestService, []);
serviceManager.register('skillManagementService', skillManagementService, []);

// Integration services (higher-level)
serviceManager.register('businessIntegrationService', businessIntegrationService, [
  'businessApiService',
  'businessProfileApiService'
]);
serviceManager.register('businessProfessionalIntegration', businessProfessionalIntegration, [
  'businessApiService'
]);
serviceManager.register('comprehensiveIntegrationService', comprehensiveIntegrationService, [
  'databaseService',
  'apiService',
  'syncService'
]);
serviceManager.register('dashboardIntegrationService', dashboardIntegrationService, []);
serviceManager.register('databaseIntegrationService', databaseIntegrationService, [
  'databaseService'
]);

// Learning services
serviceManager.register('learningApiService', learningApiService, []);

// Utility services
serviceManager.register('cacheManager', cacheManager, []);
serviceManager.register('imageUploadService', imageUploadService, []);

// Job search APIs (exported as objects)
serviceManager.register('jobSearchAPI', jobSearchAPI, []);
serviceManager.register('googleJobsAPI', googleJobsAPI, []);
serviceManager.register('fullTimeJobsAPI', fullTimeJobsAPI, []);
serviceManager.register('partTimeJobsAPI', partTimeJobsAPI, []);
serviceManager.register('entryLevelJobsAPI', entryLevelJobsAPI, []);

// Previously added services
serviceManager.register('businessNavigationService', businessNavigationService, []);
serviceManager.register('adzunaAPI', adzunaAPI, []);
serviceManager.register('aiInterviewService', aiInterviewService, []);
serviceManager.register('aiService', aiService, []);
serviceManager.register('aIVideoInterviewService', aIVideoInterviewService, []);
serviceManager.register('allJobsSearchAPI', allJobsSearchAPI, []);
serviceManager.register('apiClient', apiClient, []);
serviceManager.register('scaleUpAPI', scaleUpAPI, []);
serviceManager.register('dataManagementService', dataManagementService, []);
serviceManager.register('learningApiClient', learningApiClient, []);
serviceManager.register('schedulingService', schedulingService, []);
serviceManager.register('sqliteService', sqliteService, []);

serviceManager.register('activityTracker', activityTracker, []);
serviceManager.register('unifiedIntegrationService', unifiedIntegrationService, []);
serviceManager.register('unifiedJobApplicationService', UnifiedJobApplicationService, []);
serviceManager.register('unifiedJobAPI', unifiedJobAPI, []);

// New services from recent files
serviceManager.register('websiteAPI', websiteAPI, []);
serviceManager.register('analyticsService', analyticsService, []);

export default serviceManager;